/**
 * An UNCONFIRMED chat workflow proposal, as it is saved and read back (MAR-988).
 *
 * Decision (Alex, 2026-10-01): no `.docx` exists until the user confirms the `run_workflow` card, and
 * an unconfirmed proposal still survives a reload with whatever the user edited on it. So the
 * proposal is not a run — a run is what renders the document. It is the `run_workflow` tool call
 * MAR-991 already persists (`tool_calls`), and this is what the card writes onto that row's output:
 * `PUT /api/conversations/:id/workflow-proposals/:callId` with this body, read back on load as
 * `output.value.proposal`.
 *
 * `values` is the WHOLE set the card shows (already coerced to each input's type), not a patch: a
 * reload rebuilds the card from it alone. `status` records a Cancelar so a reload does not offer a
 * declined proposal again. A confirmed one needs no status here — confirming creates the run, and a
 * card with a run is rebuilt from the run.
 */
import { z } from 'zod/v4'

export const WORKFLOW_PROPOSAL_STATUSES = ['pending', 'declined'] as const
export type WorkflowProposalStatus = typeof WORKFLOW_PROPOSAL_STATUSES[number]

export const workflowProposalSchema = z.strictObject({
  values: z.record(z.string(), z.unknown()),
  status: z.enum(WORKFLOW_PROPOSAL_STATUSES),
})

export type WorkflowProposal = z.infer<typeof workflowProposalSchema>

/** The saved proposal on a persisted `run_workflow` call's reported output, if there is one. */
export function proposalFromReport(report: unknown): WorkflowProposal | null {
  const proposal = (report as { proposal?: unknown } | null | undefined)?.proposal
  const parsed = workflowProposalSchema.safeParse(proposal)
  return parsed.success ? parsed.data : null
}
